import { computed, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, from, switchMap } from 'rxjs';
import { validate as isUuid } from 'uuid';
import * as fromAssistants from '../assistants';
import { RouterService } from '../shared/services/router.service';
import {
  ChatActions,
  chatsFeature,
  ChatsState,
  ChatViewWithRequiredTitle,
} from '../store';

interface ConversationParams {
  chatId: number | null;
  assistantUuid: string | null;
}

/**
 * Service responsible for synchronizing conversation URL with chat state.
 * Handles URL updates for existing chats and assistant validation/assignment for empty chats.
 */
@Injectable({
  providedIn: 'root',
})
export class ConversationUrlSyncService {
  private readonly router = inject(Router);
  private readonly routerService = inject(RouterService);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly assistantsStore =
    inject<Store<fromAssistants.AssistantsState>>(Store);

  private readonly stateLoaded = computed(
    () =>
      this.assistantsStore.selectSignal(
        fromAssistants.assistantsFeature.selectLoaded,
      )() && this.chatsStore.selectSignal(chatsFeature.selectLoaded)(),
  );
  private readonly hasAnEmptyChat = this.chatsStore.selectSignal(
    chatsFeature.selectHasAnEmptyChat,
  );
  private readonly selectedChat = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChat,
  );

  constructor() {
    // Listen to navigation end events to sync URL with chat state
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(() => !!this.stateLoaded()),
        filter(() => this.hasAnEmptyChat()),
        // Only handle conversation routes
        filter(
          (event) =>
            event instanceof NavigationEnd &&
            event.url.startsWith('/chats/conversation'),
        ),
        switchMap(() => from(this.syncUrlWithState())),
      )
      .subscribe();
  }

  /**
   * Synchronizes the current URL with the state of the selected chat.
   * Always ensures chatId is in the URL.
   */
  public async syncUrlWithState() {
    const { params, hasAssistantUuidInUrl } = this.getRouteParams();
    const selectedChat = this.selectedChat();

    // 1. Selection Phase: Ensure the correct chat is selected in the Store
    const selectionResult = this.ensureChatSelected(params, selectedChat);

    // Determine which chat to use for URL normalization:
    // - If a selection was dispatched, use the actual chat data from the store
    // - Otherwise, use the current selected chat
    const chatForNormalization = selectionResult.selectedChatId
      ? this.getChatById(selectionResult.selectedChatId)
      : selectedChat;

    if (!chatForNormalization) {
      return;
    }

    // 2. Normalization Phase: Ensure the URL structure matches the chat status
    // Always ensure chatId is in URL and valid
    if (
      await this.normalizeUrlPattern(
        params,
        chatForNormalization,
        hasAssistantUuidInUrl,
      )
    ) {
      return; // Early exit if navigation was triggered
    }

    // 3. Detail Sync Phase: Ensure the assistant state matches the URL (for empty chats only)
    // Only sync assistant for empty chats - existing chats should not be updated
    // Skip if we just dispatched a selection (assistant was set during selection)
    if (!selectionResult.dispatched && selectedChat?._count.messages === 0) {
      this.syncEmptyChatAssistant(params, selectedChat);
    }
  }

  private getRouteParams() {
    // Extract route data
    let currentRoute = this.router.routerState.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const routeParamChatId = currentRoute.snapshot.params['id'] as unknown;
    const routeQueryAssistantUuid = currentRoute.snapshot.queryParams[
      'assistantUuid'
    ] as unknown;

    const params: ConversationParams = {
      chatId: routeParamChatId ? Number(routeParamChatId) : null,
      assistantUuid:
        routeQueryAssistantUuid &&
        typeof routeQueryAssistantUuid === 'string' &&
        isUuid(routeQueryAssistantUuid)
          ? routeQueryAssistantUuid
          : null,
    };

    // Check if URL actually has assistantUuid query param (even if invalid)
    const hasAssistantUuidInUrl =
      routeQueryAssistantUuid !== undefined && routeQueryAssistantUuid !== null;

    return { params, hasAssistantUuidInUrl };
  }

  /**
   * Phase 1: Handles selection logic.
   * Ensures the correct chat is selected based on URL parameters.
   * Returns the selected chat ID when a dispatch occurs (to avoid signal timing issues).
   */
  private ensureChatSelected(
    params: ConversationParams,
    selectedChat: {
      id: number;
      _count: { messages: number };
      assistantId?: string | null;
    } | null,
  ): { dispatched: boolean; selectedChatId?: number } {
    const chatId = params.chatId;

    // If chatId is valid (not null, > 0, and exists in store)
    if (chatId && this.isChatIdValid(chatId)) {
      if (chatId !== selectedChat?.id) {
        // Check if the target chat is empty - if so, use selectEmptyChatWithAssistant
        // to ensure the assistant is set correctly
        const targetChat = this.getChatById(chatId);
        if (targetChat?._count.messages === 0) {
          if (params.assistantUuid) {
            this.chatsStore.dispatch(
              ChatActions.selectEmptyChatWithAssistant({
                assistantUuid: params.assistantUuid,
              }),
            );
          } else {
            this.chatsStore.dispatch(ChatActions.selectEmptyChat());
          }
        } else {
          this.chatsStore.dispatch(ChatActions.selectChat({ id: chatId }));
        }
        return { dispatched: true, selectedChatId: chatId };
      }
      // ChatId is valid and matches selected chat - nothing to do
      return { dispatched: false };
    }

    // ChatId is invalid or doesn't exist - select/create empty chat
    if (!selectedChat || selectedChat._count.messages > 0) {
      return this.selectEmptyChat(params.assistantUuid);
    }

    return { dispatched: false };
  }

  /**
   * Dispatches action to select an empty chat, optionally with an assistant.
   */
  private selectEmptyChat(assistantUuid: string | null): {
    dispatched: boolean;
    selectedChatId?: number;
  } {
    const emptyChatId = this.findEmptyChatId();

    if (assistantUuid) {
      this.chatsStore.dispatch(
        ChatActions.selectEmptyChatWithAssistant({ assistantUuid }),
      );
    } else {
      this.chatsStore.dispatch(ChatActions.selectEmptyChat());
    }

    if (emptyChatId !== undefined) {
      return { dispatched: true, selectedChatId: emptyChatId };
    }
    return { dispatched: true };
  }

  /**
   * Finds the ID of an empty chat (no messages) in the store.
   * This mirrors the logic in the reducer for selectEmptyChat/selectEmptyChatWithAssistant.
   */
  private findEmptyChatId(): number | undefined {
    const chats = this.chatsStore.selectSignal(chatsFeature.selectAll)();
    return chats.find((chat) => chat._count.messages === 0)?.id;
  }

  /**
   * Gets a chat by its ID from the store.
   */
  private getChatById(
    chatId: number,
  ): Pick<ChatViewWithRequiredTitle, 'id' | 'assistantId' | '_count'> | null {
    return this.chatsStore.selectSignal(chatsFeature.selectChatById(chatId))();
  }

  /**
   * Phase 2: Handles router-based URL normalization.
   * Always ensures chatId is in the URL and valid.
   * Validates assistant before any navigation.
   */
  private async normalizeUrlPattern(
    params: ConversationParams,
    chat: Pick<ChatViewWithRequiredTitle, 'id' | 'assistantId' | '_count'>,
    hasAssistantUuidInUrl: boolean,
  ): Promise<boolean> {
    const isExistingChat = chat._count.messages > 0;

    // 1. Validate assistant first (for empty chats with assistant in URL)
    if (
      !isExistingChat &&
      params.assistantUuid &&
      !this.isAssistantValid(params.assistantUuid)
    ) {
      return await this.routerService.navigateToNotFound();
    }

    // 2. Ensure chatId is in URL and valid
    if (!this.isChatIdValid(params.chatId)) {
      return await this.navigateWithChatId(chat.id, params.assistantUuid);
    }

    // 3. For existing chats, ensure URL matches chat's assistant
    if (isExistingChat) {
      return await this.normalizeExistingChatUrl(
        chat,
        params.assistantUuid,
        hasAssistantUuidInUrl,
      );
    }

    return false;
  }

  /**
   * Checks if the given chatId exists in the store.
   */
  private isChatIdValid(chatId: number | null): boolean {
    return (
      chatId !== null &&
      chatId > 0 &&
      !!this.chatsStore.selectSignal(chatsFeature.selectChatById(chatId))()
    );
  }

  /**
   * Checks if the given assistant UUID exists in the store.
   */
  private isAssistantValid(assistantUuid: string): boolean {
    return !!this.assistantsStore.selectSignal(
      fromAssistants.assistantsFeature.selectEntities,
    )()[assistantUuid];
  }

  /**
   * Navigates to conversation with the given chatId and optional assistantUuid.
   */
  private async navigateWithChatId(
    chatId: number,
    assistantUuid: string | null,
  ): Promise<boolean> {
    if (assistantUuid) {
      return await this.routerService.navigateToConversationWithChatAndAssistant(
        chatId,
        assistantUuid,
        { replaceUrl: true },
      );
    }
    return await this.routerService.navigateToConversationWithChat(chatId, {
      replaceUrl: true,
    });
  }

  /**
   * Normalizes URL for existing chats to match the chat's assistant.
   */
  private async normalizeExistingChatUrl(
    chat: Pick<ChatViewWithRequiredTitle, 'id' | 'assistantId'>,
    urlAssistantUuid: string | null,
    hasAssistantUuidInUrl: boolean,
  ): Promise<boolean> {
    // Determine if URL needs normalization
    const needsAssistantRemoval = !chat.assistantId && hasAssistantUuidInUrl;
    const needsAssistantUpdate =
      chat.assistantId && chat.assistantId !== urlAssistantUuid;

    // No normalization needed
    if (!needsAssistantRemoval && !needsAssistantUpdate) {
      return false;
    }

    // Navigate to the correct URL
    if (chat.assistantId) {
      return await this.routerService.navigateToConversationWithChatAndAssistant(
        chat.id,
        chat.assistantId,
        { replaceUrl: true },
      );
    }

    return await this.routerService.navigateToConversationWithChat(chat.id, {
      replaceUrl: true,
    });
  }

  /**
   * Phase 3: Handles store updates for empty chat assistants.
   * Updates the chat's assistant to match the URL.
   * Only updates if there's a mismatch to avoid unnecessary dispatches.
   */
  private syncEmptyChatAssistant(
    params: ConversationParams,
    chat: Pick<ChatViewWithRequiredTitle, 'id' | 'assistantId'>,
  ): void {
    // Clear assistant if chat has one and no assistant UUID in URL
    if (!params.assistantUuid && chat.assistantId) {
      this.chatsStore.dispatch(
        ChatActions.updateChatAssistant({
          chatId: chat.id,
          assistantUuid: null,
        }),
      );
      return;
    }

    // Update assistant only if URL has assistant and it doesn't match the chat
    // Note: selectEmptyChatWithAssistant already sets the assistant, so this
    // should only run if there's a mismatch (e.g., URL changed after selection)
    if (params.assistantUuid && chat.assistantId !== params.assistantUuid) {
      this.chatsStore.dispatch(
        ChatActions.updateChatAssistant({
          chatId: chat.id,
          assistantUuid: params.assistantUuid,
        }),
      );
    }
    // If assistant already matches, do nothing (avoid unnecessary updates)
  }
}
